import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with UTC plugin
dayjs.extend(utc);

/**
 * Format date/time for display
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateTime = (date) => {
  if (!date) return 'N/A';
  // Format as UTC to ensure consistent display
  return dayjs.utc(date).format('MMM DD, YYYY HH:mm') + ' UTC';
};

/**
 * Format duration in hours to human-readable format
 * @param {number} hours - Duration in hours
 * @returns {string} Formatted duration (e.g., "5h 30m")
 */
export const formatDuration = (hours) => {
  if (hours == null || Number.isNaN(Number(hours))) return 'N/A';
  const totalMinutes = Math.round(Number(hours) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

/**
 * Format a display-timezone dayjs for short clock labels (e.g. "19:30").
 * Includes date when not the same calendar day as `relativeTo`.
 * @param {import('dayjs').Dayjs|null|undefined} d
 * @param {import('dayjs').Dayjs|null|undefined} [relativeTo]
 * @returns {string}
 */
export const formatClock = (d, relativeTo) => {
  if (!d || !d.isValid?.()) return '—';
  if (relativeTo?.isValid?.() && !d.isSame(relativeTo, 'day')) {
    return d.format('DD MMM HH:mm');
  }
  return d.format('HH:mm');
};

/**
 * Last landing for the day: duty ends 30 minutes after landing (operational / DGCA post-flight window).
 * @param {import('dayjs').Dayjs} dutyEnd
 * @returns {import('dayjs').Dayjs|null}
 */
export const deriveLastLandingTime = (dutyEnd) => {
  if (!dutyEnd?.isValid?.()) return null;
  return dutyEnd.subtract(30, 'minute');
};

/**
 * Earliest next duty start after required rest (max of duty length and standard min rest, default 12h).
 * @param {import('dayjs').Dayjs} dutyEnd
 * @param {number} dutyHours
 * @param {number} [minRestHours=12]
 * @returns {{ restHours: number, nextDutyStart: import('dayjs').Dayjs }|null}
 */
export const deriveEarliestNextDutyStart = (dutyEnd, dutyHours, minRestHours = 12) => {
  if (!dutyEnd?.isValid?.()) return null;
  const restHours = Math.max(Number(dutyHours) || 0, Number(minRestHours) || 12);
  return {
    restHours,
    nextDutyStart: dutyEnd.add(Math.round(restHours * 60), 'minute')
  };
};

/**
 * Baseline helicopter max FDP hours (DGCA defaults) for UI estimates.
 * @param {string} crewComplement
 * @param {number} numberOfLandings
 * @returns {number}
 */
export const estimateHelicopterBaseFdpHours = (crewComplement, numberOfLandings) => {
  if (crewComplement === 'SINGLE_PILOT') return 10;
  const landings = Number(numberOfLandings) || 0;
  if (landings === 1) return 13;
  if (landings >= 2) return 12.5;
  return 13;
};

/**
 * Split-duty FDP extension (half of break) when break is 3–10 hours.
 * @param {number} breakHours
 * @returns {number}
 */
export const getSplitDutyExtensionHours = (breakHours) => {
  const duration = Number(breakHours);
  if (!(duration >= 3 && duration <= 10)) return 0;
  return duration / 2;
};

/**
 * Build duty end-time comparison with vs without split duty.
 * @param {object} params
 * @param {import('dayjs').Dayjs} params.dutyStart
 * @param {import('dayjs').Dayjs} params.dutyEnd
 * @param {boolean} params.isSplitDuty
 * @param {import('dayjs').Dayjs|null} [params.breakStart]
 * @param {import('dayjs').Dayjs|null} [params.breakEnd]
 * @param {number} params.baseFdpHours
 * @param {number} [params.minRestHours=12]
 * @returns {object|null}
 */
export const buildSplitDutyEndReflection = ({
  dutyStart,
  dutyEnd,
  isSplitDuty,
  breakStart,
  breakEnd,
  baseFdpHours,
  minRestHours = 12
}) => {
  if (!dutyStart?.isValid?.() || !dutyEnd?.isValid?.() || !dutyEnd.isAfter(dutyStart)) return null;

  const dutyMinutes = dutyEnd.diff(dutyStart, 'minute');
  const dutyHours = dutyMinutes / 60;
  const maxEndWithout = dutyStart.add(Math.round(baseFdpHours * 60), 'minute');

  let breakMinutes = 0;
  let extensionHours = 0;
  let segment1Hours = 0;
  let segment2Hours = 0;
  if (isSplitDuty && breakStart?.isValid?.() && breakEnd?.isValid?.() && breakEnd.isAfter(breakStart)) {
    breakMinutes = breakEnd.diff(breakStart, 'minute');
    extensionHours = getSplitDutyExtensionHours(breakMinutes / 60);
    if (!breakStart.isBefore(dutyStart) && !breakEnd.isAfter(dutyEnd)) {
      segment1Hours = breakStart.diff(dutyStart, 'minute') / 60;
      segment2Hours = dutyEnd.diff(breakEnd, 'minute') / 60;
    }
  }

  const extendedFdpHours = baseFdpHours + extensionHours;
  const maxEndWith = dutyStart.add(Math.round(extendedFdpHours * 60), 'minute');

  const lastLandingEntered = deriveLastLandingTime(dutyEnd);
  const nextAfterEntered = deriveEarliestNextDutyStart(dutyEnd, dutyHours, minRestHours);
  const lastLandingWithout = deriveLastLandingTime(maxEndWithout);
  const nextAfterWithout = deriveEarliestNextDutyStart(maxEndWithout, baseFdpHours, minRestHours);
  const lastLandingWith = deriveLastLandingTime(maxEndWith);
  const nextAfterWith = deriveEarliestNextDutyStart(maxEndWith, extendedFdpHours, minRestHours);

  return {
    dutyHours,
    baseFdpHours,
    extensionHours,
    extendedFdpHours,
    breakHours: breakMinutes / 60,
    segment1Hours,
    segment2Hours,
    maxEndWithout,
    maxEndWith,
    withinWithout: dutyHours <= baseFdpHours + 1e-9,
    withinWith: dutyHours <= extendedFdpHours + 1e-9,
    lastLandingEntered,
    nextDutyStartEntered: nextAfterEntered?.nextDutyStart || null,
    restHoursEntered: nextAfterEntered?.restHours ?? minRestHours,
    lastLandingWithout,
    nextDutyStartWithout: nextAfterWithout?.nextDutyStart || null,
    restHoursWithout: nextAfterWithout?.restHours ?? minRestHours,
    lastLandingWith,
    nextDutyStartWith: nextAfterWith?.nextDutyStart || null,
    restHoursWith: nextAfterWith?.restHours ?? minRestHours
  };
};

/**
 * Get Material-UI color for status chip
 * @param {string} status - Status value
 * @returns {string} Color name
 */
export const getStatusColor = (status) => {
  switch (status) {
    case 'SCHEDULED': return 'info';
    case 'IN_PROGRESS': return 'warning';
    case 'COMPLETED': return 'success';
    case 'CANCELLED': return 'error';
    default: return 'default';
  }
};

/**
 * Label suffix for roster / flight duty location (4-letter code or first 4 chars of city).
 * @param {string} [city]
 * @param {string} [explicitCode] - optional A–Z code, up to 4 chars
 * @returns {string} e.g. "Beng" for Bengaluru when code omitted
 */
export function getDutyWorkLocationDisplayCode(city, explicitCode) {
  const rawCode = explicitCode != null ? String(explicitCode).trim() : '';
  const lettersOnly = rawCode.toUpperCase().replace(/[^A-Z]/g, '');
  if (lettersOnly.length > 0) return lettersOnly.slice(0, 4);
  const c = city != null ? String(city).trim() : '';
  if (c.length === 0) return '';
  return c.slice(0, 4);
}

/**
 * Aircraft registration / model for a FLIGHT (or flight-training) duty entry.
 * Prefers sector aircraft, then training aircraft, then any nested aircraftId.
 */
export function getDutyAircraftInfo(entry) {
  const ac =
    entry?.sectorId?.aircraftId ||
    entry?.trainingAircraftId ||
    entry?.aircraftId ||
    null;
  if (!ac || typeof ac !== 'object') {
    return { registration: '', model: '', label: '' };
  }
  const registration = String(ac.registration || '').trim();
  const model = String(ac.model || '').trim();
  const manufacturer = String(ac.manufacturer || '').trim();
  const modelLabel = [manufacturer, model].filter(Boolean).join(' ').trim() || model;
  let label = '';
  if (registration && modelLabel) label = `${registration} · ${modelLabel}`;
  else if (registration) label = registration;
  else if (modelLabel) label = modelLabel;
  return { registration, model: modelLabel, label };
}

/** Chip label for planning FLIGHT row: registration (or Flight) + optional location code */
export function getRosterFlightChipLabel(entry) {
  if (entry?.dutyType === 'SCHEDULED_DUTY_OFF') return 'Off';
  const code = getDutyWorkLocationDisplayCode(entry?.dutyWorkLocationCity, entry?.dutyWorkLocationCode);
  const { registration } = getDutyAircraftInfo(entry);
  const parts = [registration || 'Flight'];
  if (code) parts.push(code);
  return parts.length > 1 ? parts.join(' · ') : parts[0];
}

/** Stable key for city → same key = same palette color (prefer full city name) */
export function cityKeyForPalette(city, explicitCode) {
  const cityStr = city != null ? String(city).trim() : '';
  if (cityStr.length > 0) return cityStr.toLowerCase();
  const code = getDutyWorkLocationDisplayCode(city, explicitCode);
  return code ? code.toLowerCase() : '';
}

function hashStringToIndex(str, modulo) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h) % modulo;
}

/** Pastel backgrounds for city code segment; same city key → same entry */
const CITY_CODE_PALETTE = [
  { bg: '#e3f2fd', fg: '#0d47a1' },
  { bg: '#e8f5e9', fg: '#1b5e20' },
  { bg: '#fff3e0', fg: '#e65100' },
  { bg: '#f3e5f5', fg: '#4a148c' },
  { bg: '#e0f7fa', fg: '#006064' },
  { bg: '#fce4ec', fg: '#880e4f' },
  { bg: '#f1f8e9', fg: '#33691e' },
  { bg: '#fff8e1', fg: '#f57f17' },
  { bg: '#e8eaf6', fg: '#1a237e' },
  { bg: '#efebe9', fg: '#3e2723' },
  { bg: '#e1f5fe', fg: '#01579b' },
  { bg: '#f9fbe7', fg: '#827717' },
  { bg: '#ede7f6', fg: '#311b92' },
  { bg: '#e0f2f1', fg: '#004d40' },
  { bg: '#fffde7', fg: '#f57f17' },
  { bg: '#e8eaf6', fg: '#283593' },
  { bg: '#ffebee', fg: '#b71c1c' },
  { bg: '#e3f2fd', fg: '#1565c0' },
  { bg: '#e8f5e9', fg: '#2e7d32' },
  { bg: '#f3e5f5', fg: '#6a1b9a' },
  { bg: '#e1bee7', fg: '#4a148c' },
  { bg: '#c8e6c9', fg: '#1b5e20' },
  { bg: '#bbdefb', fg: '#0d47a1' },
  { bg: '#d0d9ff', fg: '#1a237e' },
  { bg: '#ffe0b2', fg: '#e65100' },
  { bg: '#f8bbd0', fg: '#880e4f' },
  { bg: '#b2dfdb', fg: '#004d40' },
  { bg: '#d7ccc8', fg: '#3e2723' },
  { bg: '#cfd8dc', fg: '#263238' },
  { bg: '#ffcdd2', fg: '#b71c1c' },
  { bg: '#b3e5fc', fg: '#01579b' },
  { bg: '#dcedc8', fg: '#33691e' },
  { bg: '#fff9c4', fg: '#f57f17' }
];

/**
 * MUI sx for the city-code segment (deterministic per city key).
 */
export function getCityCodeSegmentSx(city, explicitCode) {
  const key = cityKeyForPalette(city, explicitCode);
  if (!key) {
    return { bgcolor: '#eceff1', color: '#455a64' };
  }
  const idx = hashStringToIndex(key, CITY_CODE_PALETTE.length);
  const p = CITY_CODE_PALETTE[idx];
  return { bgcolor: p.bg, color: p.fg };
}

/** VIP / IR / HILL / CP / TRI / TRE / CHPL / ATPL / Dual Type cleared crew: gold flight segment; otherwise light blue */
export function getFlightSegmentSx(highlightCleared) {
  if (highlightCleared === true) {
    return { bgcolor: '#ffd54f', color: 'rgba(0, 0, 0, 0.82)' };
  }
  return { bgcolor: '#e3f2fd', color: '#1565c0' };
}

/** Outer border color for the combined flight + city chip */
export function getRosterFlightChipOutlineColor(highlightCleared) {
  return highlightCleared === true ? '#f9a825' : '#2196f3';
}

/** @deprecated Use RosterFlightDutyChip + getFlightSegmentSx — kept for any legacy imports */
export const FLIGHT_DUTY_CHIP_SX = {
  maxWidth: 92,
  height: 22,
  fontSize: '0.7rem',
  bgcolor: '#ffd54f',
  border: '1px solid #f9a825',
  color: 'rgba(0, 0, 0, 0.82)',
  fontWeight: 600
};
